import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getAllContacts, getContactById, createContact, markContactAsReplied, deleteContact } from '../api/contactsUsAPI';
import { useState } from 'react';

export const useContacts = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  const [currentPage, setCurrentPage] = useState(page);
  const [pageSize, setPageSize] = useState(limit);

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['contacts', currentPage, pageSize],
    () => getAllContacts(currentPage, pageSize),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch contacts';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: false,
      staleTime: 300000, // Consider data fresh for 5 minutes
      cacheTime: 3600000, // Keep data in cache for 1 hour
    }
  );

  return {
    contacts: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchContacts: refetch,
    setPage: setCurrentPage,
    setLimit: setPageSize
  };
};

export const useContact = (contactId) => {
  const { setError } = useAuthStore();

  const {
    data: contact,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['contact', contactId],
    () => getContactById(contactId),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to fetch contact details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: contactId !== undefined && contactId !== null,
    }
  );

  return {
    contact,
    isLoading,
    error,
    refetchContact: refetch
  };
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  
  const mutation = useMutation(
    (contactId) => deleteContact(contactId),
    {
      onSuccess: () => {
        toast.success('Contact deleted successfully');
        queryClient.invalidateQueries('contacts');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to delete contact';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );
  
  return mutation;
};

export const useCreateContact = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(
    (contactData) => createContact(contactData),
    {
      onSuccess: () => {
        toast.success('Your message has been sent successfully!');
        queryClient.invalidateQueries('contacts');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to send your message';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );

  return mutation;
};

export const useMarkAsReplied = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  
  const mutation = useMutation(
    // Updated to accept both contactId and replyContent
    ({ contactId, replyContent }) => markContactAsReplied(contactId, replyContent),
    {
      onSuccess: () => {
        toast.success('Reply sent successfully');
        queryClient.invalidateQueries('contacts');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to send reply';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );
  
  return mutation;
};